import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import './Ticker.scss';

interface TickerProps {
  /** Scroll speed in pixels per second. Matches react-malarquee's `rate`. */
  rate?: number;
  children: React.ReactNode;
}

/**
 * A horizontally scrolling ticker, replacing react-malarquee.
 *
 * The library this stands in for animated by calling setState on every
 * animation frame, so the player screen re-rendered sixty times a second for
 * as long as a venue left it running. Here the scroll is a CSS animation on
 * the compositor and JavaScript runs only when the size changes.
 *
 * The content is repeated enough times to cover the container, and the track
 * slides left by exactly one copy's width before the animation restarts, so
 * the loop has no visible seam.
 */
/** Enough to cover a wall-sized screen with short ticker text. */
const MAX_COPIES = 12;

const Ticker = ({ rate = 60, children }: TickerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLSpanElement>(null);
  const [copies, setCopies] = useState(2);
  const [distance, setDistance] = useState(0);

  const measure = useCallback(() => {
    const container = containerRef.current;
    const copy = copyRef.current;
    if (!container || !copy) return;

    const copyWidth = copy.offsetWidth;
    // Fonts may not have loaded yet, in which case measuring is pointless.
    if (!copyWidth) return;

    setDistance(copyWidth);
    // Capped as a backstop. The stylesheet stops the box growing with its own
    // content, but if some future layout reintroduces that, this turns a
    // runaway into a ticker that is merely too long.
    setCopies(Math.min(MAX_COPIES, Math.max(2, Math.ceil(container.offsetWidth / copyWidth) + 1)));
  }, []);

  useLayoutEffect(measure);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    if (containerRef.current) observer.observe(containerRef.current);
    if (copyRef.current) observer.observe(copyRef.current);
    return () => observer.disconnect();
  }, [measure]);

  // One copy's width at `rate` pixels a second. Zero until the first measure,
  // which leaves the ticker still rather than flickering at some wrong speed.
  const duration = distance > 0 ? distance / rate : 0;

  const style = {
    '--jk-ticker-distance': `${distance}px`,
    animationDuration: duration > 0 ? `${duration}s` : undefined,
    animationPlayState: duration > 0 ? 'running' : 'paused',
  } as React.CSSProperties;

  return (
    <div className="jk-ticker" ref={containerRef}>
      <div className="jk-ticker__track" style={style}>
        {Array.from({ length: copies }, (unused, i) => (
          <span className="jk-ticker__copy" key={i} ref={i === 0 ? copyRef : undefined} aria-hidden={i > 0}>
            {children}
          </span>
        ))}
      </div>
    </div>
  );
};

export default Ticker;
